<script setup lang="ts">
import { useField, useForm, useIsFormDirty, useIsFormValid } from "vee-validate";
import * as yup from "yup";

import { ALERT_STATUS } from "~/common/interfaces/alert-status.enum";
import type { UpdateTagByIdDto } from "~/common/interfaces/dto/tags/update-tag-by-id.dto";
import { useAlertStore } from "~/stores/alert";
import { useModalStore } from "~/stores/modal";
import { useTagsStore } from "~/stores/tags";

const { t } = useI18n();
const route = useRoute();
const alertStore = useAlertStore();
const tagsStore = useTagsStore();
const { tagsModalEdit } = useModalStore();
const { errors, handleSubmit } = useForm<UpdateTagByIdDto>({
	initialValues: {
		name: tagsModalEdit.tag.name
	},
	validationSchema: yup.object({
		name: yup
			.string()
			.required(t("INPUT.name.error.required"))
	})
});
const isFormDirty = useIsFormDirty();
const isFormValid = useIsFormValid();

const onSubmit = handleSubmit(async (dto: UpdateTagByIdDto) => {
	tagsStore.currentTag = await tagsStore.patchById(tagsModalEdit.tag.id, dto);

	tagsModalEdit.closeModal();

	if (route.name === "products-tags") {
		tagsStore.getAll({}, tagsStore.recentGetAllQuery);
	}

	alertStore.addAlert({
		title: t("COMPONENTS.TAGS.MODAL_EDIT.alert.title"),
		description: t("COMPONENTS.TAGS.MODAL_EDIT.alert.description", { tagName: tagsModalEdit.tag.name }),
		status: ALERT_STATUS.SUCCESS
	});
});

const { value: name } = useField<string>("name");
</script>

<template>
	<AppModal
		:title="$t('COMPONENTS.TAGS.MODAL_EDIT.title')"
		:close-button-text="$t('COMMON.cancel')"
		:submit-button-text="$t('COMMON.save')"
		:submit-button-active="isFormValid && isFormDirty"
		@close="tagsModalEdit.closeModal"
		@submit="onSubmit"
	>
		<span class="font-semibold text-base">
			{{ $t('COMMON.generalInfo') }}
		</span>
		<div class="mt-4 flex flex-col gap-y-2">
			<AppInput
				v-model="name"
				type="text"
				:title="$t('COMMON.name')"
				:placeholder="$t('INPUT.PLACEHOLDER.tag')"
				:error="errors.name"
				required
			/>
		</div>
	</AppModal>
</template>
